import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ContactBlock } from "@/components/ContactInfo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { MessageSquare, Phone, Mail, MapPin, ShieldCheck } from "lucide-react";

const SmsConsent = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        path="/sms-consent"
        title="SMS & Email Consent — In Paradise Beauty"
        description="Understand how In Paradise Beauty uses SMS and email for appointment communications and how to opt out."
      />
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Legal</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">
              SMS & Email Consent
            </h1>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground font-light leading-[2] tracking-wide space-y-8">
            <p className="text-xs text-muted-foreground/70 uppercase tracking-[0.3em]">
              Last Updated: February 21, 2026
            </p>

            <div className="border border-primary/30 p-8 not-prose bg-card">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck size={24} className="text-primary" />
                <h2 className="font-serif text-xl font-semibold text-foreground italic">Purpose</h2>
              </div>
              <p className="text-muted-foreground text-sm font-light leading-[2]">
                In Paradise Beauty uses SMS (text messaging) and email communication to provide appointment-related information and business communications. By creating an account and booking with us, you authorize In Paradise Beauty to contact you via text message and email using the contact information you provide.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">
                <MessageSquare size={18} className="inline mr-2 text-primary" />
                What Communications You May Receive
              </h2>
              <p>By opting in, you agree to receive recurring SMS text messages and emails from In Paradise Beauty, which may include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Appointment confirmations</li>
                <li>Appointment reminders</li>
                <li>Scheduling updates or changes</li>
                <li>Post-visit care instructions and review requests</li>
                <li>Billing, deposit, and payment receipts</li>
                <li>Business announcements or closures</li>
                <li>Promotional offers and marketing messages (only if separately opted in)</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Message Frequency</h2>
              <p>
                Message frequency varies. You can typically expect up to <strong className="text-foreground">5 transactional messages per appointment</strong> (confirmation, reminder, receipt, review request, and any schedule changes). If you opt in to marketing messages, you may receive up to <strong className="text-foreground">4 promotional messages per month</strong>.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Message & Data Rates</h2>
              <p>
                <strong className="text-foreground">Message and data rates may apply.</strong> Charges are determined by your mobile carrier plan. In Paradise Beauty is not responsible for any charges incurred from receiving SMS communications.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Opt-Out Instructions</h2>
              <p>You may opt out of communications at any time:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong className="text-foreground">SMS:</strong> Reply <strong className="text-foreground">STOP</strong>, <strong className="text-foreground">END</strong>, <strong className="text-foreground">CANCEL</strong>, <strong className="text-foreground">UNSUBSCRIBE</strong>, or <strong className="text-foreground">QUIT</strong> to any text message. You will receive a one-time confirmation that you have been unsubscribed and will not receive further messages.</li>
                <li><strong className="text-foreground">Email:</strong> Click the unsubscribe link in any email.</li>
                <li>Contact the office directly to be removed from all communications.</li>
              </ul>
              <p>
                After opting out, you will no longer receive marketing or transactional text messages except for legally required notifications. To resume messages, reply <strong className="text-foreground">START</strong> or <strong className="text-foreground">UNSTOP</strong>, or opt in again from your account.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Help & Support</h2>
              <p>
                For assistance, reply <strong className="text-foreground">HELP</strong> to any text message and you will receive a reply with the business name and contact information. You can also reach us directly:
              </p>
              <ContactBlock />
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Privacy & Data Use</h2>
              <p>
                Your phone number, email address, and personal information will be used solely for appointment and business communication purposes. In Paradise Beauty will not sell, share, or distribute your information to third parties for marketing purposes.
              </p>
              <p>
                All communications comply with applicable telecommunications regulations, including Twilio A2P 10DLC messaging requirements and the CAN-SPAM Act.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">Consent Acknowledgment</h2>
              <p>By creating an account and booking an appointment with In Paradise Beauty, you acknowledge that:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>You are the authorized user of the phone number and email address provided.</li>
                <li>You consent to receive SMS and email communications from In Paradise Beauty.</li>
                <li>You understand you may opt out at any time.</li>
                <li>You understand that message and data rates may apply.</li>
              </ul>
            </div>

            <div className="border-t border-border pt-6">
              <p className="text-xs text-muted-foreground/70">
                This consent policy complies with Twilio A2P 10DLC messaging requirements and general communication regulations.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default SmsConsent;
